"use client";

import Image from "next/image";
import Reveal from "../../enterprise/components/Reveal";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";

const DemocracySection = () => {
  const t = useTranslations("governance");
  const getStyle = (label: string) => {
    switch (label) {
      case t('for'):
        return {
          dotUrl: "/images/icons/dot-gov3.svg",
          bar: "bg-[#30A46C]",
          text: "text-[#30A46C]",
        };
      case t('against'):
        return {
          dotUrl: "/images/icons/dot-gov.svg",
          bar: "bg-[#E5484D]",
          text: "text-[#E5484D]",
        };
      case t('abstain'):
        return {
          dotUrl: "/images/icons/dot-gov2.svg",
          bar: "bg-[#8D8D8D]",
          text: "text-[#8D8D8D]",
        };
      default:
        return {
          dotUrl: "/images/icons/dot-gov.svg",
          bar: "bg-[#30A46C]",
          text: "text-[#30A46C]",
        };
    }
  };
  const data = {
    status: t("active"),
    proposal_id: "HIP-24",
    title: t("fund-community-compute-credits"),
    description: t(
      "allocate-2m-hyra-from-treasury-to-subsidize-model-inference-for-citizens",
    ),
    options: [
      {
        label: t('for'),
        percentage: 79.9,
        votes: "79.9% votes",
        color: "green",
      },
      {
        label: t('against'),
        percentage: 17.1,
        votes: "17.1% votes",
        color: "red",
      },
      {
        label: t('abstain'),
        percentage: 3.3,
        votes: "3.3% votes",
        color: "gray",
      },
    ],
    meta: {
      turnout: "14%",
      quorum: "20%",
      time_left: "5h 29m",
    },
  };

  const timelineData = [
    {
      step: 1,
      label: t('start'),
      date: "Sep 13,",
      time: "10:26 AM",
    },
    {
      step: 2,
      label: "Snapshot",
      date: "Sep 13,",
      time: "12:26 PM",
    },
    {
      step: 3,
      label: t('ends'),
      date: "Sep 13,",
      time: "10:26 PM",
    },
    {
      step: 4,
      label: t('execution'),
      date: "Sep 13,",
      time: "11:26 PM",
    },
  ];

  const resultData = [
    {
      id: 1,
      status: t('success'),
      proposal_id: "HIP-23",
      name: "Adopt Constitution v1.1",
      description: t('final-tally-demo'),
      verify: "Passed",
    },
    {
      id: 2,
      status: t('success'),
      proposal_id: "HIP-23",
      name: "Adopt Constitution v1.1",
      description: t('final-tally-demo'),
      verify: "Passed",
    },
  ];
  return (
    <section className="mx-auto max-w-[1440px] px-[78px] py-12 max-lg:p-0">
      <div className="text-center max-sm:p-4">
        <Reveal
          delay={0.1}
          duration={0.8}
          direction="left"
          withBlur={true}
          blurAmount={8}
        >
          <h2 className="text-[48px]/[56px] tracking-[-1px] text-[#202020] max-lg:text-[32px]/[40px] max-sm:text-2xl">
            {t("real-time-democracy-on-chain")}
          </h2>
        </Reveal>
        <Reveal
          delay={0.5}
          duration={0.8}
          direction="left"
          withBlur={true}
          blurAmount={8}
        >
          <p className="mx-auto mt-6 max-w-[640px] text-lg font-light text-[#202020] max-lg:text-sm">
            {t(
              "at-the-heart-of-hyra-lies-its-digital-constitution-a-living-document-that-enshrines-community-rights-and-principles-0",
            )}
          </p>
        </Reveal>
      </div>
      <div className="mt-14 flex gap-6 rounded-[56px] bg-gradient-to-b from-[#EFF5FF] to-transparent p-10 max-lg:flex-col max-md:p-4">
        {/* left */}
        <motion.div
          initial={{ opacity: 0, translateY: 40 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          className="flex flex-col gap-[30px] rounded-3xl bg-white p-6 max-lg:rounded-[54px]"
        >
          {/* <div className="p-6 flex flex-col gap-[30px] bg-white rounded-3xl"> */}
          <div className="flex items-center gap-2">
            <p className="text-[32px]/[40px] tracking-[-1px] text-[#202020]">
              {t("active-proposals")}
            </p>
            <p className="flex h-fit items-center justify-center rounded-full border border-[#008F4A]/[32%] bg-[#008F4A]/[8%] px-3 text-base font-light text-[#008F4A]/[80%]">
              Live
            </p>
          </div>
          <div className="">
            <div className="flex items-center gap-2">
              <p className="flex h-fit items-center justify-center rounded-full border border-[#008F4A]/[32%] bg-[#008F4A]/[8%] px-3 text-base font-light text-[#008F4A]/[80%]">
                {data.status}
              </p>
              <p className="text-base text-[#202020]">{data.proposal_id}</p>
            </div>
            <h3 className="mt-2 text-2xl tracking-[-0.5px] text-[#202020] max-sm:text-lg">
              {data.title}
            </h3>
            <p className="mt-2 text-base font-light text-[#646464]">
              {data.description}
            </p>
          </div>
          {/* Options */}
          {data.options.map((opt: any) => {
            const style = getStyle(opt.label);
            return (
              <div key={opt.label}>
                <div className="mb-1 flex justify-between text-sm font-medium">
                  <span className="flex items-center gap-2 text-lg text-[#202020]">
                    <Image
                      src={style.dotUrl}
                      width={12}
                      height={12}
                      alt={opt.label}
                    />
                    {opt.label}
                  </span>
                  <span className="text-lg text-[#202020]">
                    {opt.percentage}%
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-2 rounded-full ${style.bar}`}
                    style={{ width: `${opt.percentage}%` }}
                  />
                </div>
                <p className={`mt-1 text-sm ${style.text}`}>{opt.percentage} {t('votes')}</p>
              </div>
            );
          })}

          {/* Meta */}
          <div className="flex justify-start gap-6 text-lg text-[#646464]">
            <p>
              {t('turnout')}{" "}
              <span className="text-[#202020]">{data.meta.turnout}</span>
            </p>
            <p>
              {t('quorum')} <span className="text-[#202020]">{data.meta.quorum}</span>
            </p>
            <p className="text-[#202020]">{data.meta.time_left} {t('left')}</p>
          </div>
          {/* </div> */}
        </motion.div>

        {/* right */}
        <motion.div
          initial={{ opacity: 0, translateY: 40 }}
          whileInView={{ opacity: 1, translateY: 0 }}
          transition={{ delay: 0.4, duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          className="flex h-full w-1/2 flex-col gap-6 max-lg:w-full"
        >
          {/* <div className="w-1/2 h-full flex flex-col gap-6"> */}
          {/* right top */}
          <div className="h-[40%] rounded-3xl bg-white p-6">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[32px]/[40px] tracking-[-1px] text-[#202020] max-sm:text-lg">
                {t("voting-timeline")}
              </h2>
              <span className="text-base text-[#202020]">HIP-24</span>
            </div>

            {/* Progress bar */}
            <div className="relative mb-6 h-2 w-full rounded-full bg-gray-200">
              <div
                className="absolute left-0 top-0 h-2 rounded-full bg-green-500"
                style={{ width: "50%" }} // fix cứng tạm thời
              />
            </div>

            {/* Steps */}
            <div className="grid w-full grid-cols-4 justify-between max-sm:grid-cols-1 max-sm:gap-6">
              {timelineData.map((item) => (
                <div key={item.step} className="flex items-start gap-2">
                  <div className="flex aspect-square h-9 w-9 items-center justify-center rounded-full bg-black/[6%] text-base text-[#202020]">
                    {item.step}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-lg text-[#202020]">
                      {item.label}
                    </div>
                    <div className="max-sm:flex">
                      <div className="text-sm font-light text-[#646464]">
                        {item.date}
                      </div>
                      <div className="text-sm font-light text-[#646464]">
                        {item.time}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* right bot */}
          <div className="h-[60%] rounded-3xl bg-white p-6">
            <h3 className="text-[32px]/[40px] tracking-[-1px] text-[#202020] max-sm:text-lg">
              {t("latest-result")}
            </h3>
            <div>
              {resultData.map((item) => (
                <div
                  key={item.id}
                  className="border-b border-black/20 pb-4 pt-6"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <p className="rounded-full bg-[#30A46C] px-2 py-1 text-xs text-white">
                        {item.status}
                      </p>
                      <p className="ml-2">{item.proposal_id}</p>
                    </div>
                    <span className="flex h-fit items-center justify-center rounded-full border border-[#008F4A]/[32%] bg-[#008F4A]/[8%] px-3 text-xs font-light text-[#008F4A]/[80%]">
                      {item.verify}
                    </span>
                  </div>
                  <h4 className="mt-1 text-lg text-[#202020]">{item.name}</h4>
                  <p className="text-[#646464]mt-1 text-sm font-light">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          {/* </div> */}
        </motion.div>
      </div>
    </section>
  );
};

export default DemocracySection;
